import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { formatMoney } from "@/lib/quotes";
import ImageCropDialog from "@/components/ImageCropDialog";
import { Camera, ImagePlus, Trash2, X, Loader2, ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";

const MAX_DIM = 1400;
const JPEG_Q = 0.85;

// Read a File to a compressed data URL (used as *input* to the cropper).
async function fileToDataUrl(file) {
  const img = await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const el = new Image();
    el.onload = () => { URL.revokeObjectURL(url); resolve(el); };
    el.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    el.src = url;
  });
  const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", JPEG_Q);
}

const emptyItem = { description: "", quantity: "", unit_price: "", image_b64: null };
const makeItem = () => ({ ...emptyItem, _key: `item_${Math.random().toString(36).slice(2, 10)}` });
const emptyForm = {
  customer: { name: "", phone: "", address: "" },
  items: [makeItem()],
  discount: 0,
  note: "",
};

function ItemImage({ value, onChange, testid, onPickSource }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const handle = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      onPickSource(dataUrl);
    } catch (e) { toast.error("Couldn't read the image"); }
  };

  if (value) {
    return (
      <div className="relative h-24 w-24 rounded-md overflow-hidden border border-border shrink-0" data-testid={`${testid}-preview`}>
        <img src={value} alt="" className="h-full w-full object-cover" />
        <button
          type="button"
          onClick={() => onChange(null)}
          className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white grid place-items-center"
          data-testid={`${testid}-remove`}
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="h-24 w-24 rounded-md border-2 border-dashed border-border grid grid-rows-2 gap-1 p-1 shrink-0">
      <button
        type="button"
        onClick={() => cameraRef.current?.click()}
        data-testid={`${testid}-camera`}
        className="flex flex-col items-center justify-center rounded-sm hover:bg-black/5 dark:hover:bg-white/5 text-muted-foreground"
      >
        <Camera className="h-4 w-4" />
        <span className="text-[9px] uppercase tracking-widest mt-0.5">Camera</span>
      </button>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        data-testid={`${testid}-file`}
        className="flex flex-col items-center justify-center rounded-sm hover:bg-black/5 dark:hover:bg-white/5 text-muted-foreground"
      >
        <ImagePlus className="h-4 w-4" />
        <span className="text-[9px] uppercase tracking-widest mt-0.5">Gallery</span>
      </button>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => { handle(e.target.files?.[0]); e.target.value = ""; }}
      />
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { handle(e.target.files?.[0]); e.target.value = ""; }}
      />
    </div>
  );
}

export default function QuoteFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [cropTarget, setCropTarget] = useState(null); // { idx, source }
  const [discountMode, setDiscountMode] = useState("amount"); // "amount" | "percent"
  const [discountPercent, setDiscountPercent] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/quotes/${id}`);
        if (data.status !== "sent") {
          toast.error("This quotation is no longer editable");
          navigate(`/quotes/${id}`);
          return;
        }
        setForm({
          customer: data.customer,
          items: data.items.map((it) => ({
            _key: `item_${Math.random().toString(36).slice(2, 10)}`,
            description: it.description,
            quantity: String(it.quantity),
            unit_price: String(it.unit_price),
            image_b64: it.image_b64 || null,
          })),
          discount: data.discount || 0,
          note: data.note || "",
        });
      } catch (e) { toast.error(formatApiError(e)); navigate("/quotes"); }
      finally { setLoading(false); }
    })();
  }, [id, isEdit, navigate]);

  const setItem = (idx, patch) => {
    setForm((f) => {
      const items = f.items.map((it, i) => i === idx ? { ...it, ...patch } : it);
      return { ...f, items };
    });
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, makeItem()] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const { subtotal, final } = useMemo(() => {
    const st = form.items.reduce((acc, it) => acc + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), 0);
    const d = parseFloat(form.discount) || 0;
    return { subtotal: st, final: Math.max(0, st - d) };
  }, [form]);

  // Keep discount amount + percent in sync
  const setDiscountAmount = (amt) => {
    setForm((f) => ({ ...f, discount: amt }));
    if (subtotal > 0) {
      const p = (parseFloat(amt) || 0) * 100 / subtotal;
      setDiscountPercent(p ? p.toFixed(2).replace(/\.?0+$/, "") : "");
    } else {
      setDiscountPercent("");
    }
  };
  const setDiscountPct = (pct) => {
    setDiscountPercent(pct);
    const p = parseFloat(pct) || 0;
    const amt = subtotal * p / 100;
    setForm((f) => ({ ...f, discount: amt ? amt.toFixed(2) : 0 }));
  };

  const submit = async (e) => {
    e.preventDefault();
    // Validate
    if (!form.customer.name.trim() || !form.customer.phone.trim() || !form.customer.address.trim()) {
      toast.error("Customer name, phone and address are required");
      return;
    }
    const cleanItems = form.items
      .map((it) => ({
        description: (it.description || "").trim(),
        quantity: parseFloat(it.quantity),
        unit_price: parseFloat(it.unit_price),
        image_b64: it.image_b64 || null,
      }))
      .filter((it) => it.description && !isNaN(it.quantity) && it.quantity > 0 && !isNaN(it.unit_price) && it.unit_price >= 0);
    if (cleanItems.length === 0) {
      toast.error("Add at least one item with description, quantity and price");
      return;
    }
    setSaving(true);
    try {
      const body = {
        customer: form.customer,
        items: cleanItems,
        discount: parseFloat(form.discount) || 0,
        note: form.note?.trim() || null,
      };
      if (isEdit) {
        await api.put(`/quotes/${id}`, body);
        toast.success("Quotation updated");
        navigate(`/quotes/${id}`);
      } else {
        const { data } = await api.post("/quotes", body);
        toast.success(`Quotation ${data.quote_no} created`);
        navigate(`/quotes/${data.quote_id}`);
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" /> Loading…
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="px-4 pt-4 pb-24 space-y-6" data-testid="quote-form">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground font-mono-mini"
      >
        <ArrowLeft className="h-3 w-3" /> Back
      </button>
      <div>
        <div className="font-mono-mini text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          {isEdit ? "Edit" : "New"}
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
          {isEdit ? "Edit quotation" : "New quotation"}
        </h1>
      </div>

      {/* Customer */}
      <Card className="p-4 rounded-2xl border-border shadow-soft space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Customer</div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
          <Input
            data-testid="cust-name"
            value={form.customer.name}
            onChange={(e) => setForm({ ...form, customer: { ...form.customer, name: e.target.value } })}
            className="mt-1.5 h-10"
            required
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone</Label>
          <Input
            data-testid="cust-phone"
            type="tel"
            inputMode="tel"
            value={form.customer.phone}
            onChange={(e) => setForm({ ...form, customer: { ...form.customer, phone: e.target.value } })}
            className="mt-1.5 h-10 font-mono-mini"
            required
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Address</Label>
          <Textarea
            data-testid="cust-address"
            rows={2}
            value={form.customer.address}
            onChange={(e) => setForm({ ...form, customer: { ...form.customer, address: e.target.value } })}
            className="mt-1.5"
            required
          />
        </div>
      </Card>

      {/* Items */}
      <div className="space-y-3" data-testid="items-section">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Line items</div>
          <button
            type="button"
            onClick={addItem}
            data-testid="add-item-btn"
            className="text-[11px] uppercase tracking-widest font-medium text-foreground hover:opacity-70"
          >
            + Add line
          </button>
        </div>
        {form.items.map((it, idx) => {
          const lineTotal = (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0);
          return (
            <Card key={it._key || idx} className="p-3 rounded-2xl border-border shadow-soft" data-testid={`item-${idx}`}>
              <div className="flex gap-3">
                <ItemImage
                  value={it.image_b64}
                  onChange={(v) => setItem(idx, { image_b64: v })}
                  onPickSource={(src) => setCropTarget({ idx, source: src })}
                  testid={`item-${idx}-image`}
                />
                <div className="flex-1 space-y-2 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono-mini text-[10px] uppercase tracking-widest text-muted-foreground">
                      Sr {idx + 1}
                    </div>
                    {form.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        data-testid={`item-${idx}-remove`}
                        className="text-destructive hover:opacity-70"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <Input
                    placeholder="Description"
                    data-testid={`item-${idx}-desc`}
                    value={it.description}
                    onChange={(e) => setItem(idx, { description: e.target.value })}
                    className="h-9"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Qty</Label>
                      <Input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        data-testid={`item-${idx}-qty`}
                        value={it.quantity}
                        onChange={(e) => setItem(idx, { quantity: e.target.value })}
                        className="mt-1 h-9 tabular-nums"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Unit ₹</Label>
                      <Input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        data-testid={`item-${idx}-price`}
                        value={it.unit_price}
                        onChange={(e) => setItem(idx, { unit_price: e.target.value })}
                        className="mt-1 h-9 tabular-nums"
                      />
                    </div>
                  </div>
                  <div className="text-right text-xs font-medium">
                    Total <span className="font-mono-mini tabular-nums ml-2" data-testid={`item-${idx}-total`}>
                      {formatMoney(lineTotal)}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Totals */}
      <Card className="p-4 rounded-2xl border-border shadow-soft space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="font-mono-mini tabular-nums" data-testid="form-subtotal">{formatMoney(subtotal)}</span>
        </div>

        {/* Discount — amount vs percent */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Discount</span>
            <div
              className="inline-flex rounded-md border border-border overflow-hidden text-[10px] uppercase tracking-widest"
              role="tablist"
            >
              <button
                type="button"
                data-testid="discount-mode-amount"
                onClick={() => setDiscountMode("amount")}
                className={`px-2.5 py-1 ${discountMode === "amount" ? "bg-foreground text-background" : "text-muted-foreground"}`}
              >
                ₹
              </button>
              <button
                type="button"
                data-testid="discount-mode-percent"
                onClick={() => setDiscountMode("percent")}
                className={`px-2.5 py-1 ${discountMode === "percent" ? "bg-foreground text-background" : "text-muted-foreground"}`}
              >
                %
              </button>
            </div>
          </div>
          {discountMode === "amount" ? (
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              data-testid="form-discount"
              value={form.discount}
              onChange={(e) => setDiscountAmount(e.target.value)}
              className="h-10 tabular-nums text-right"
              placeholder="₹ off"
            />
          ) : (
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              max="100"
              data-testid="form-discount-percent"
              value={discountPercent}
              onChange={(e) => setDiscountPct(e.target.value)}
              className="h-10 tabular-nums text-right"
              placeholder="% off"
            />
          )}
          <p className="text-[10px] text-muted-foreground mt-1.5">
            The % is a calculation aid only — the quotation shows the amount, not the percentage.
          </p>
        </div>

        <div className="pt-2 border-t border-border flex items-center justify-between">
          <span className="text-sm font-medium">Final price</span>
          <span
            className="font-display text-2xl font-bold tabular-nums"
            style={{ color: "hsl(var(--status-leave))" }}
            data-testid="form-final"
          >
            {formatMoney(final)}
          </span>
        </div>
      </Card>

      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Note (optional)</Label>
        <Textarea
          data-testid="form-note"
          rows={2}
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          className="mt-1.5"
          placeholder="Delivery terms, validity…"
        />
      </div>

      <Button
        type="submit"
        disabled={saving}
        data-testid="quote-form-submit"
        className="w-full h-12 text-white btn-lift"
        style={{ background: "hsl(var(--status-leave))" }}
      >
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
        {isEdit ? "Save changes" : "Send quotation"}
      </Button>

      {/* Image crop dialog */}
      <ImageCropDialog
        open={!!cropTarget}
        source={cropTarget?.source}
        onCancel={() => setCropTarget(null)}
        onCropped={(dataUrl) => {
          if (cropTarget != null) {
            setItem(cropTarget.idx, { image_b64: dataUrl });
          }
          setCropTarget(null);
        }}
      />
    </form>
  );
}
